import { clone } from '@jack-henry/frontend-utils/functions';
import {
    GuidKeyCollectionDto,
    NotificationConfigurationFeatureModelDto,
    NotificationConfigurationGroupModelDto,
    NotificationConfigurationModelDto,
    ThresholdDto,
} from '@treasury/api/channel';
import { DomainEntity } from '../../../../shared';

export class NotificationThreshold extends DomainEntity<ThresholdDto> {
    constructor(dto = clone({} as ThresholdDto)) {
        super(dto);
    }

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get id() {
        return this.dto.id;
    }

    public get underAmount() {
        return this.dto.underAmount;
    }

    public get overAmount() {
        return this.dto.overAmount;
    }

    public isDeleted() {
        return this.dto.isDeleted;
    }

    public get accountIds() {
        return this.dto.accountIds as GuidKeyCollectionDto;
    }
}

export class NotificationConfigurationFeature extends DomainEntity<NotificationConfigurationFeatureModelDto> {
    constructor(dto = clone({} as NotificationConfigurationFeatureModelDto)) {
        super(dto);
    }

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get name() {
        return this.dto.name;
    }

    public get isDesktopRequired() {
        return this.dto.isDesktopRequired;
    }

    public set isDesktopRequired(value: boolean) {
        this.dto.isDesktopRequired = value;
    }

    public get isActive() {
        return this.dto.isActive;
    }

    public set isActive(value: boolean) {
        this.dto.isActive = value;
    }

    public get isChanged() {
        return this.dto.isChanged;
    }

    public set isChanged(value: boolean) {
        this.dto.isChanged = value;
    }

    public get isEmailAllowed() {
        return this.dto.isEmailAllowed;
    }

    public get isEmailActive() {
        return this.dto.isEmailActive;
    }

    public set isEmailActive(value: boolean) {
        this.dto.isEmailActive = value;
    }

    public get isEmailRequired() {
        return this.dto.isEmailRequired;
    }

    public get isDesktopAllowed() {
        return this.dto.isDesktopAllowed;
    }

    public get isDesktopActive() {
        return this.dto.isDesktopActive;
    }

    public set isDesktopActive(value: boolean) {
        this.dto.isDesktopActive = value;
    }

    public get isTextMessageActive() {
        return this.dto.isTextMessageActive;
    }

    public set isTextMessageActive(value: boolean) {
        this.dto.isTextMessageActive = value;
    }

    public get isTextMessageAllowed() {
        return this.dto.isTextMessageAllowed;
    }

    public get isTextMessageRequired() {
        return this.dto.isTextMessageRequired;
    }

    public get notificationTypeId() {
        return this.dto.notificationTypeId;
    }

    public get sequence() {
        return this.dto.sequence;
    }

    public get sortOrder() {
        return this.dto.sortOrder;
    }

    public get threshold() {
        return new NotificationThreshold(this.dto.threshold);
    }
}

export class NotificationConfigurationGroup extends DomainEntity<NotificationConfigurationGroupModelDto> {
    constructor(dto = clone({} as NotificationConfigurationGroupModelDto)) {
        super(dto);
    }

    private _filteredFeatures: NotificationConfigurationFeature[] =
        this.dto.features
            ?.map(f => new NotificationConfigurationFeature(f))
            .sort((a, b) => a.sequence - b.sequence) || [];

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get name() {
        return this.dto.name;
    }

    public get features() {
        const sortedFeatures =
            this.dto.features
                ?.map(f => new NotificationConfigurationFeature(f))
                .sort((a, b) => a.sequence - b.sequence) || [];
        this.filteredFeatures = sortedFeatures;
        return sortedFeatures;
    }

    public get filteredFeatures() {
        return this._filteredFeatures;
    }

    public set filteredFeatures(value: NotificationConfigurationFeature[]) {
        this._filteredFeatures = value;
    }
}

export class NotificationConfiguration extends DomainEntity<NotificationConfigurationModelDto> {
    constructor(dto = clone({} as NotificationConfigurationModelDto)) {
        super(dto);
    }

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get email() {
        return this.dto.email;
    }

    public set email(value: string) {
        this.dto.email = value;
    }

    public get isDuplicateEmail() {
        return this.dto.isDuplicateEmail;
    }

    public get phoneNumber() {
        return this.dto.phoneNumber;
    }

    public set phoneNumber(value: string | undefined) {
        this.dto.phoneNumber = value;
    }

    public get isFirstTimeLoad() {
        return this.dto.isFirstTimeLoad;
    }

    public get groups() {
        return (
            this.dto.groups
                ?.map(g => new NotificationConfigurationGroup(g))
                .sort((a, b) => a.name.localeCompare(b.name)) || []
        );
    }

    public getFeatureByName(name: string): NotificationConfigurationFeature | undefined {
        return this.groups.flatMap(group => group.features).find(feature => feature.name === name);
    }
}
